import * as React from 'react';

import { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';

import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { ExternalCoachAction } from 'bundles/ai-coach-platform/components/patterns/chat/ExternalCoachAction';
import StyledCoachTriggerButtonWithAnimationOnMount from 'bundles/unified-common/components/coach-on-discovery/StyledCoachTriggerButtonWithAnimationOnMount';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useCoachCareerFitAssistantData } from 'bundles/unified-description-page-common/providers/specialization/CoachCareerFitAssistantProvider';

import _t from 'i18n!nls/xdp';

const COACH_MODE_ID = 'XDP_CAREER_FIT_ASSISTANT';
const COACH_BUTTON_ACTION_ID = 'IS_THIS_RIGHT_FOR_ME';
const COACH_INTENTION_ROUTER_ACTION_ID = 'ROUTER';

const CoachButton = ({ className }: { className?: string }) => {
  const { setCoachOpen, setContextConfig } = useCoachState();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: 'coach_trigger_button',
      linkName: `${COACH_MODE_ID}_${COACH_BUTTON_ACTION_ID}`.toLowerCase(),
    },
  });

  const { coachCareerFitAssistantEnabled } = useCoachCareerFitAssistantData();
  const specializationPageData = useSpecializationPageData();
  const careerGrowthInPercentage = specializationPageData?.careerOutcomes?.careerGrowthInPercentage?.toString() ?? '';

  if (!coachCareerFitAssistantEnabled || !specializationPageData) {
    return null;
  }

  const label = _t('Is this right for me?');
  const ariaLabel =
    specializationPageData.productVariant === DescriptionPageProductVariant.ProfessionalCertificate
      ? _t('Ask Coursera: is this certificate right for me?')
      : _t('Ask Coursera: is this specialization right for me?');

  const coachActionButtonConfig = {
    courseraContext: {
      specializationId: specializationPageData.id,
    },
    config: {
      coachModeId: COACH_MODE_ID,
      coachActionId: COACH_BUTTON_ACTION_ID,
    },
    message: label,
    additionalContext: [
      {
        contextKey: 'additionalContext.productSlug',
        contextValue: specializationPageData.slug,
      },
      {
        contextKey: 'additionalContext.productType',
        contextValue: 'SPECIALIZATION',
      },
      ...(careerGrowthInPercentage
        ? [
            {
              contextKey: 'additionalContext.careerGrowthInPercentage',
              contextValue: `percentage-of-certificate-grads-report-career-improvement: ${careerGrowthInPercentage}%`,
            },
          ]
        : []),
    ],
  };

  return (
    <div ref={visibilityTrackingRef}>
      <ExternalCoachAction
        size="small"
        key="coach"
        newSessionOnClick={false}
        onClick={() => {
          const { message, ...coachChatContext } = coachActionButtonConfig;
          setContextConfig({
            ...coachChatContext,
            config: {
              coachModeId: COACH_MODE_ID,
              coachActionId: COACH_INTENTION_ROUTER_ACTION_ID,
            },
          });
          setCoachOpen(true);
        }}
        aria-label={ariaLabel}
        className={className}
        label={label}
        {...coachActionButtonConfig}
        customCoachTriggerButton={StyledCoachTriggerButtonWithAnimationOnMount}
      />
    </div>
  );
};

export default CoachButton;
